/**
 * Parse a date string (either ISO timestamp or YYYY-MM-DD format) to Unix timestamp
 * @param dateStr - Date string in ISO format or YYYY-MM-DD format
 * @returns Unix timestamp in milliseconds
 */
export function parseLocalDate(dateStr: string): number {
  if (dateStr.includes('T')) {
    // ISO timestamp format - parse directly
    return new Date(dateStr).getTime();
  } else {
    // YYYY-MM-DD format - parse as local date
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).getTime();
  }
}

/**
 * Normalize a date to 20:00 UTC for consistent timezone display
 * @param dateStr - Date string in any format
 * @returns ISO string normalized to 20:00 UTC
 */
export function normalizeToUtc20(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    // Check if date is valid
    if (isNaN(d.getTime())) {
      // If invalid, return the original date string
      return dateStr;
    }
    // Force to 20:00 UTC
    d.setUTCHours(20, 0, 0, 0);
    return d.toISOString();
  } catch (error) {
    // If any error occurs, return the original date string
    return dateStr;
  }
} 

/**
 * Return the Monday (00:00 local) timestamp for the week that contains tsMs.
 * Weeks are Monday..Sunday and the display date should be Monday.
 */
export function getWeekStartMonday(tsMs: number): number {
  const d = new Date(tsMs);
  const day = d.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const daysSinceMonday = (day + 6) % 7; // Mon->0, Tue->1, ..., Sun->6
  const monday = new Date(d);
  monday.setDate(d.getDate() - daysSinceMonday);
  monday.setHours(0, 0, 0, 0);
  return monday.getTime();
}

/**
 * Reduce a time series to weekly buckets (Monday labels), keeping the latest point within each week.
 * The returned points have time set to Monday-of-week and the chosen value from the latest close within that week.
 */
export function reduceToWeeklyLatest<T extends { time: number }>(
  series: T[],
  valueKey: keyof T
): Array<{ time: number } & Pick<T, typeof valueKey>> {
  const buckets = new Map<number, { time: number; value: any; latestTime: number }>();
  for (const pt of series) {
    if (typeof pt.time !== 'number') continue;
    const bucket = getWeekStartMonday(pt.time);
    const current = buckets.get(bucket);
    if (!current || pt.time > current.latestTime) {
      buckets.set(bucket, { time: bucket, value: (pt as any)[valueKey], latestTime: pt.time });
    }
  }
  const out: Array<{ time: number } & any> = Array.from(buckets.values())
    .sort((a, b) => a.time - b.time)
    .map(b => ({ time: b.time, [valueKey]: b.value }));
  return out as any;
}

/**
 * Get the start date for a given time window
 * @param window - Time window key ('1m', '3m', '6m', '1y', '2y')
 * @returns Date object representing the start of the window
 */
export function getWindowStartDate(window: string): Date {
  const now = new Date();
  const daysByWindow: Record<string, number> = {
    '1m': 30,
    '3m': 90,
    '6m': 180,
    '1y': 365,
    '2y': 730,
  };
  const days = daysByWindow[window] ?? 180;
  const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  startDate.setHours(0, 0, 0, 0);
  return startDate;
}